var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { formatUnits } from "@ethersproject/units";
import { AnimatePresence, motion } from "framer";
import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { BarChartIcon, GlobeIcon } from "../../../assets/icons/icons";
import { BaseButton, SecondaryText, Subtitle, Title, } from "../../../designSystem";
import theme from "../../../designSystem/theme";
import { useLatestAPY } from "../../../hooks/useAirtableData";
import useTextAnimation from "../../../hooks/useTextAnimation";
import useVaultData from "../../../hooks/useVaultData";
import { getAssetLogo } from "../../../utils/asset";
import { formatSignificantDecimals } from "../../../utils/math";
import { getVaultColor } from "../../../utils/vault";
import CapBar from "../../Deposit/CapBar";
import { productCopies } from "../productCopies";
import YieldComparison from "./YieldComparison";
var FrameContainer = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  perspective: 2000px;\n"], ["\n  perspective: 2000px;\n"])));
var Frame = styled(motion.div)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  flex-wrap: wrap;\n  width: 294px;\n  height: 429px;\n  position: relative;\n  padding: 18px;\n  border-radius: ", ";\n  background: linear-gradient(\n    96.84deg,\n    ", "29 1.04%,\n    ", "0A 98.99%\n  );\n  transition: 0.25s border-color ease-out, 0.25s box-shadow ease-out;\n\n  &:hover {\n    padding: 16px;\n    box-shadow: ", "66 8px 16px 80px;\n    border: 2px ", " ", ";\n  }\n"], ["\n  display: flex;\n  flex-direction: column;\n  flex-wrap: wrap;\n  width: 294px;\n  height: 429px;\n  position: relative;\n  padding: 18px;\n  border-radius: ", ";\n  background: linear-gradient(\n    96.84deg,\n    ", "29 1.04%,\n    ", "0A 98.99%\n  );\n  transition: 0.25s border-color ease-out, 0.25s box-shadow ease-out;\n\n  &:hover {\n    padding: 16px;\n    box-shadow: ", "66 8px 16px 80px;\n    border: 2px ", " ", ";\n  }\n"])), theme.border.radius, function (props) { return props.color; }, function (props) { return props.color; }, function (props) { return props.color; }, theme.border.style, function (props) { return props.color; });
var TagContainer = styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  z-index: 1;\n  margin-right: auto;\n"], ["\n  z-index: 1;\n  margin-right: auto;\n"])));
var ProductTag = styled(BaseButton)(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  background: ", "29;\n  padding: 8px;\n  margin-right: 4px;\n"], ["\n  background: ", "29;\n  padding: 8px;\n  margin-right: 4px;\n"])), function (props) { return props.color; });
var ModeSwitcherContainer = styled.div(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: 32px;\n  height: 32px;\n  border-radius: 100px;\n  background: ", "14;\n  z-index: 1;\n"], ["\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: 32px;\n  height: 32px;\n  border-radius: 100px;\n  background: ", "14;\n  z-index: 1;\n"])), function (props) { return props.color; });
var ProjectedAPYLabel = styled(SecondaryText)(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n  font-size: 12px;\n  line-height: 16px;\n  margin-right: auto;\n"], ["\n  font-size: 12px;\n  line-height: 16px;\n  margin-right: auto;\n"])));
var ProjectedAPYData = styled(Title)(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n  font-size: 14px;\n  line-height: 20px;\n  color: ", ";\n"], ["\n  font-size: 14px;\n  line-height: 20px;\n  color: ", ";\n"])), function (props) { return props.color; });
var YieldFrame = function (_a) {
    var vault = _a.vault, onClick = _a.onClick;
    var _b = useVaultData(vault), status = _b.status, deposits = _b.deposits, vaultLimit = _b.vaultLimit, asset = _b.asset, displayAsset = _b.displayAsset, decimals = _b.decimals;
    var isLoading = useMemo(function () { return status === "loading"; }, [status]);
    var color = getVaultColor(vault);
    var Logo = getAssetLogo(displayAsset);
    var latestAPY = useLatestAPY(vault);
    var loadingText = useTextAnimation(!latestAPY.fetched);
    var perfStr = latestAPY.fetched
        ? latestAPY.res.toFixed(2) + "%"
        : loadingText;
    var totalDepositStr = isLoading
        ? 0
        : parseFloat(formatSignificantDecimals(formatUnits(deposits, decimals), 2));
    var depositLimitStr = isLoading
        ? 1
        : parseFloat(formatSignificantDecimals(formatUnits(vaultLimit, decimals)));
    var _c = useState("info"), mode = _c[0], setMode = _c[1];
    var onSwapMode = useCallback(function (e) {
        e.stopPropagation();
        setMode(function (prev) { return (prev === "info" ? "yield" : "info"); });
    }, []);
    var logo = useMemo(function () {
        switch (displayAsset) {
            case "yvUSDC":
                return (_jsx(Logo, { height: "208", width: "auto", markerConfig: { height: 48, width: 48, right: "16px" } }, void 0));
            default:
                return _jsx(Logo, { height: "208", width: "auto" }, void 0);
        }
    }, [displayAsset, Logo]);
    var body = useMemo(function () {
        switch (mode) {
            case "info":
                return (_jsxs(_Fragment, { children: [_jsx("div", __assign({ className: "mt-4 d-flex justify-content-center" }, { children: logo }), void 0), _jsxs("div", __assign({ className: "d-flex align-items-center mt-4" }, { children: [_jsx(ProjectedAPYLabel, { children: "Projected Yield (APY)" }, void 0), _jsx(ProjectedAPYData, __assign({ color: color }, { children: perfStr }), void 0)] }), void 0), _jsx("div", __assign({ className: "mt-auto" }, { children: _jsx(CapBar, { loading: isLoading, current: totalDepositStr, cap: depositLimitStr, copies: {
                                    current: "Current Deposits",
                                    cap: "Max Capacity",
                                }, labelConfig: {
                                    fontSize: 12,
                                }, statsConfig: {
                                    fontSize: 14,
                                }, barConfig: { height: 4, extraClassNames: "my-2", radius: 2 }, asset: asset }, void 0) }), void 0)] }, void 0));
            case "yield":
                return (_jsx(YieldComparison, { vault: vault, config: {
                        background: color + "29",
                        border: "none",
                    } }, void 0));
        }
    }, [
        vault,
        logo,
        asset,
        mode,
        color,
        perfStr,
        isLoading,
        totalDepositStr,
        depositLimitStr,
    ]);
    return (_jsx(FrameContainer, __assign({ role: "button", onClick: onClick }, { children: _jsx(AnimatePresence, __assign({ exitBeforeEnter: true, initial: false }, { children: _jsxs(Frame, __assign({ transition: {
                    duration: 0.1,
                    type: "keyframes",
                    ease: "linear",
                }, initial: {
                    transform: "rotateY(90deg)",
                }, animate: {
                    transform: "rotateY(0deg)",
                }, exit: {
                    transform: "rotateY(-90deg)",
                }, color: color }, { children: [_jsxs("div", __assign({ className: "d-flex w-100" }, { children: [_jsx(TagContainer, { children: productCopies[vault].tags.map(function (tag) { return (_jsx(ProductTag, __assign({ color: color }, { children: _jsx(Subtitle, { children: tag }, void 0) }), tag)); }) }, void 0), _jsx(ModeSwitcherContainer, __assign({ role: "button", onClick: onSwapMode, color: color }, { children: mode === "info" ? (_jsx(GlobeIcon, { color: color }, void 0)) : (_jsx(BarChartIcon, { color: color }, void 0)) }), void 0)] }), void 0), body] }), mode) }), void 0) }), void 0));
};
export default YieldFrame;
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7;
//# sourceMappingURL=YieldFrame.js.map